import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  adminCreateFlexiblePlan,
  adminDeleteFlexiblePlan,
  adminGetFlexiblePlans,
  adminGetFlexiblePlansForDropDown,
  adminGetUserFlexiblePlans,
  adminUpdateFlexiblePlan,
  adminVerifyFlexiblePlanDelete,
} from './adminApi';
import { getUserFlexiblePlans } from './api';

export const FLEXIBLE_PLAN_STATUSES = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export const FLEXIBLE_PLAN_EXPIRED_IN_TYPES = {
  MINUTES: 'MINUTES',
  DAYS: 'DAYS',
  WEEKS: 'WEEKS',
  MONTHS: 'MONTHS',
  YEARS: 'YEARS',
} as const;

export type TFlexiblePlanStatuses = typeof FLEXIBLE_PLAN_STATUSES[keyof typeof FLEXIBLE_PLAN_STATUSES];
export type TFlexiblePlanExpiredInTypes = typeof FLEXIBLE_PLAN_EXPIRED_IN_TYPES[keyof typeof FLEXIBLE_PLAN_EXPIRED_IN_TYPES];

export interface IFlexiblePlanDeleteVerificationData {
  studentFlexibleRateCount: number;
}

export interface IFlexiblePlan {
  _id: string;
  name: string;
  status: TFlexiblePlanStatuses;
  numberOfCredits: number;
  features: string[];
  isMostPopular: boolean;
  expiredIn: {
    count: number;
    type: TFlexiblePlanExpiredInTypes;
  };
  amount?: number;
  currency?: string;
}

export interface IInitialFlexiblePlansState {
  status: 'idle' | 'loading' | 'rejected';
  userFlexiblePlans: Array<IFlexiblePlan>;
  // Admin
  adminFlexiblePlanListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IFlexiblePlan>;
  };
  adminFlexiblePlanListDataForDropDown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IFlexiblePlan>;
  };
  adminFlexiblePlanDeleteVerificationData?: IFlexiblePlanDeleteVerificationData;
  adminUserFlexiblePlans: IFlexiblePlan[];
}

const initialState: IInitialFlexiblePlansState = {
  status: 'idle',
  userFlexiblePlans: [],
  // Admin
  adminFlexiblePlanListData: undefined,
  adminFlexiblePlanListDataForDropDown: undefined,
  adminFlexiblePlanDeleteVerificationData: undefined,
  adminUserFlexiblePlans: [],
};

export const FlexiblePlansSlice = createSlice({
  initialState,
  name: 'flexiblePlansSlice',
  reducers: {
    resetUserFlexiblePlans: (state) => {
      state.userFlexiblePlans = [];
    },
    // Admin
    resetAdminFlexiblePlanListData: (state) => {
      state.adminFlexiblePlanListData = undefined;
    },
    resetAdminFlexiblePlanListDataForDropDown: (state) => {
      state.adminFlexiblePlanListDataForDropDown = undefined;
    },
    resetAdminFlexiblePlanDeleteVerificationData: (state) => {
      state.adminFlexiblePlanDeleteVerificationData = undefined;
    },
    resetAdminUserFlexiblePlans: (state) => {
      state.adminUserFlexiblePlans = [];
    },
  },
  extraReducers(builder) {
    builder.addCase(getUserFlexiblePlans.fulfilled, (state, action) => {
      const result = action.payload;

      state.userFlexiblePlans = result || [];
    });

    // Admins
    builder
      // FLEXIBLE PLANS
      .addCase(adminGetFlexiblePlans.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminFlexiblePlanListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminFlexiblePlanListData = newAdminFlexiblePlanListData;
      })
      .addCase(adminGetFlexiblePlansForDropDown.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminFlexiblePlanListDataForDropDown = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminFlexiblePlanListDataForDropDown = newAdminFlexiblePlanListDataForDropDown;
      })
      .addCase(adminCreateFlexiblePlan.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminFlexiblePlanListData) {
          const newAdminFlexiblePlanListData = {
            ...state.adminFlexiblePlanListData,
          };

          newAdminFlexiblePlanListData.list.unshift(result);

          if (newAdminFlexiblePlanListData.totalDocs) {
            newAdminFlexiblePlanListData.totalDocs += 1;
          } else {
            newAdminFlexiblePlanListData.totalDocs = 1;
          }

          state.adminFlexiblePlanListData = newAdminFlexiblePlanListData;
        }
      })
      .addCase(adminUpdateFlexiblePlan.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminFlexiblePlanListData &&
          state.adminFlexiblePlanListData.list &&
          state.adminFlexiblePlanListData.list.length
        ) {
          const newAdminFlexiblePlanListData = {
            ...state.adminFlexiblePlanListData,
          };

          const index = newAdminFlexiblePlanListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminFlexiblePlanListData.list[index] = result;
          }

          state.adminFlexiblePlanListData = newAdminFlexiblePlanListData;
        }
      })
      .addCase(adminDeleteFlexiblePlan.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminFlexiblePlanListData &&
          state.adminFlexiblePlanListData.list &&
          state.adminFlexiblePlanListData.list.length
        ) {
          const newAdminFlexiblePlanListData = {
            ...state.adminFlexiblePlanListData,
          };

          newAdminFlexiblePlanListData.totalDocs = newAdminFlexiblePlanListData.totalDocs
            ? newAdminFlexiblePlanListData.totalDocs - 1
            : 0;

          newAdminFlexiblePlanListData.list = newAdminFlexiblePlanListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminFlexiblePlanListData = newAdminFlexiblePlanListData;
        }
      })
      .addCase(adminVerifyFlexiblePlanDelete.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminFlexiblePlanDeleteVerificationData = result;
      })
      .addCase(adminGetUserFlexiblePlans.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminUserFlexiblePlans = result;
      });

    // Common Matchers
    builder.addMatcher(
      isPending(
        getUserFlexiblePlans,
        // Admin
        adminCreateFlexiblePlan,
        adminGetFlexiblePlans,
        adminUpdateFlexiblePlan,
        adminDeleteFlexiblePlan,
        adminVerifyFlexiblePlanDelete,
        adminGetUserFlexiblePlans
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        getUserFlexiblePlans,
        // Admin
        adminCreateFlexiblePlan,
        adminGetFlexiblePlans,
        adminUpdateFlexiblePlan,
        adminDeleteFlexiblePlan,
        adminVerifyFlexiblePlanDelete,
        adminGetUserFlexiblePlans
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        getUserFlexiblePlans,
        // Admin
        adminCreateFlexiblePlan,
        adminGetFlexiblePlans,
        adminUpdateFlexiblePlan,
        adminDeleteFlexiblePlan,
        adminVerifyFlexiblePlanDelete,
        adminGetUserFlexiblePlans
      ),
      (state) => {
        state.status = 'loading';
      }
    );
  },
});

export const getFlexiblePlansState = (
  state: RootState
): RootState['flexiblePlans'] => state.flexiblePlans;

export const {
  resetUserFlexiblePlans,
  // Admin
  resetAdminFlexiblePlanListData,
  resetAdminFlexiblePlanListDataForDropDown,
  resetAdminFlexiblePlanDeleteVerificationData,
  resetAdminUserFlexiblePlans,
} = FlexiblePlansSlice.actions;

export * from './api';
export * from './adminApi';
export default FlexiblePlansSlice.reducer;
